import { computed, ref } from 'vue'

import { ACTIVE_DAYS, NUMBER_TO_DAY } from '@/types/offer'
import { resolveAudienceScopedStoreIds, resolveIncludedAudiences } from '@/utils/audienceStoreIds'
import { fromIsoToInputDate, mapApiChannelToUi } from '@/utils/offerHelpers'
import useAudienceStoreScope from '@/composables/useAudienceStoreScope'

import type { ComputedRef } from 'vue'
import type { ActiveDay, DistributionChannel, OfferFormSnapshot, TargetAudience, TimeSlot } from '@/types/offer'
import type { Audience, LoyaltyReward, Offer } from '@/types/types'
import type { ContentCreator, ContentScope } from '@/types/contentScope'

export interface UseOfferFormOptions {
  isEditMode: ComputedRef<boolean>
}

export const useOfferForm = (options: UseOfferFormOptions) => {
  const { isEditMode } = options

  const scope = ref<ContentScope | null>(null)
  const creator = ref<ContentCreator | null>(null)
  const offerName = ref('')
  const internalDescription = ref('')
  const selectedRewardId = ref<number | null>(null)
  const rewards = ref<LoyaltyReward[]>([])
  const isSubmitting = ref(false)
  const isNavigatingAfterSave = ref(false)

  const allCustomerLists = ref<Audience[]>([])
  const targetAudience = ref<TargetAudience>('all')
  const selectedSegmentIds = ref<number[]>([])
  const excludedSegmentIds = ref<number[]>([])

  const scheduleStartDate = ref<string | null>(null)
  const scheduleEndDate = ref<string | null>(null)

  const hasScheduleError = ref(false)
  const hasTargetError = ref(false)

  const createTimeSlot = (id: number): TimeSlot => ({
    id,
    days: [...ACTIVE_DAYS],
    startTime: '',
    endTime: ''
  })

  const timeSlots = ref<TimeSlot[]>([createTimeSlot(1)])
  const selectedChannels = ref<DistributionChannel[]>([])
  const offerStoreIds = ref<number[]>([])
  const claimLimitQuantity = ref<number | null>(null)
  const claimLimitDays = ref<number | null>(null)
  const isPromotionalCodeRequired = ref(false)

  const includedAudiences = computed(() =>
    targetAudience.value === 'segment' ? resolveIncludedAudiences(allCustomerLists.value, selectedSegmentIds.value) : []
  )
  const scopedStoreIds = computed(() => resolveAudienceScopedStoreIds(includedAudiences.value))

  useAudienceStoreScope({
    scopedStoreIds,
    getSelectedIds: () => offerStoreIds.value,
    setSelectedIds: (ids) => {
      offerStoreIds.value = ids
    },
    getRestoreIds: () => [],
    emptyMeansUnrestricted: true
  })

  const selectedReward = computed(() => rewards.value.find((r) => r.id === selectedRewardId.value) ?? null)

  const submitDisabled = computed(() => {
    const hasName = offerName.value.trim().length > 0
    const hasReward = selectedRewardId.value !== null
    return !hasName || !hasReward || hasScheduleError.value || hasTargetError.value || isSubmitting.value
  })

  const getCurrentSnapshot = (): OfferFormSnapshot => ({
    scope: scope.value,
    name: offerName.value.trim(),
    description: internalDescription.value.trim(),
    rewardId: selectedRewardId.value,
    startDate: scheduleStartDate.value,
    endDate: scheduleEndDate.value,
    targetAudience: targetAudience.value,
    selectedSegmentIds: [...selectedSegmentIds.value].sort(),
    excludedSegmentIds: [...excludedSegmentIds.value].sort(),
    storeIds: [...offerStoreIds.value].sort(),
    channels: [...selectedChannels.value].sort(),
    claimLimitQuantity: claimLimitQuantity.value,
    claimLimitDays: claimLimitDays.value,
    isPromotionalCodeRequired: isPromotionalCodeRequired.value,
    timeSlots: timeSlots.value.map((slot) => ({
      days: [...slot.days].sort(),
      startTime: slot.startTime,
      endTime: slot.endTime
    }))
  })

  const initialSnapshot = ref<OfferFormSnapshot | null>(null)

  const isDirty = computed(() => {
    if (!isEditMode.value) return true
    if (!initialSnapshot.value) return false
    return JSON.stringify(initialSnapshot.value) !== JSON.stringify(getCurrentSnapshot())
  })

  const hydrateFromOffer = (existingOffer: Offer) => {
    scope.value = existingOffer.scope ?? null
    creator.value = existingOffer.creator ?? null
    offerName.value = existingOffer.name
    internalDescription.value = existingOffer.description ?? ''
    selectedRewardId.value = existingOffer.rewardId ?? null

    scheduleStartDate.value = fromIsoToInputDate(existingOffer.startDate ?? null)
    scheduleEndDate.value = fromIsoToInputDate(existingOffer.endDate ?? null)

    const hydratedStoreIds =
      existingOffer.storeIds && existingOffer.storeIds.length > 0
        ? existingOffer.storeIds
        : (existingOffer.stores?.map((store) => store.id) ?? existingOffer.reward?.storeIds ?? [])
    offerStoreIds.value = hydratedStoreIds
    claimLimitQuantity.value = existingOffer.claimLimitQuantity ?? null
    claimLimitDays.value = existingOffer.claimLimitDays ?? null
    isPromotionalCodeRequired.value = existingOffer.isPromotionalCodeRequired ?? false

    if (existingOffer.channels && existingOffer.channels.length > 0) {
      const uiChannels = existingOffer.channels.flatMap((ch) => mapApiChannelToUi(ch))
      selectedChannels.value = Array.from(new Set(uiChannels))
    } else {
      selectedChannels.value = ['in_store', 'kiosk', 'click_collect', 'online', 'delivery']
    }

    const includedIds = (existingOffer.includedSegments ?? []).map((s) => s.id).filter((id): id is number => id != null)
    const excludedIds = (existingOffer.excludedSegments ?? []).map((s) => s.id).filter((id): id is number => id != null)

    if (includedIds.length > 0) {
      targetAudience.value = 'segment'
      selectedSegmentIds.value = includedIds
    } else if (existingOffer.loyaltyProgramMemberOnly) {
      targetAudience.value = 'loyal'
      selectedSegmentIds.value = []
    } else {
      targetAudience.value = 'all'
      selectedSegmentIds.value = []
    }

    excludedSegmentIds.value = excludedIds

    if (existingOffer.availabilitySlots && existingOffer.availabilitySlots.length > 0) {
      const slots: TimeSlot[] = existingOffer.availabilitySlots.map((slot, index) => {
        const firstRange = slot.timeRanges && slot.timeRanges.length > 0 ? slot.timeRanges[0] : undefined

        const mappedDays = (slot.daysOfWeek ?? []).map((d) => NUMBER_TO_DAY[d]).filter((d): d is ActiveDay => !!d)
        const days: ActiveDay[] = mappedDays.length > 0 ? mappedDays : [...ACTIVE_DAYS]

        return {
          id: index + 1,
          days,
          startTime: firstRange?.start ?? '',
          endTime: firstRange?.end ?? ''
        }
      })

      timeSlots.value = slots.length > 0 ? slots : [createTimeSlot(1)]
    } else {
      timeSlots.value = [createTimeSlot(1)]
    }

    initialSnapshot.value = getCurrentSnapshot()
  }

  return {
    scope,
    creator,
    offerName,
    internalDescription,
    selectedRewardId,
    rewards,
    isSubmitting,
    isNavigatingAfterSave,
    allCustomerLists,
    targetAudience,
    selectedSegmentIds,
    excludedSegmentIds,
    scheduleStartDate,
    scheduleEndDate,
    hasScheduleError,
    hasTargetError,
    timeSlots,
    selectedChannels,
    offerStoreIds,
    scopedStoreIds,
    claimLimitQuantity,
    claimLimitDays,
    isPromotionalCodeRequired,

    selectedReward,
    submitDisabled,
    isDirty,
    initialSnapshot,

    createTimeSlot,
    getCurrentSnapshot,
    hydrateFromOffer
  }
}
